import i18nFactory from '@yandex-int/i18n';
import { FetchResult } from 'apollo-link';
import { get, values } from 'lodash';
import { Epic, ofType } from 'redux-observable';
import { EMPTY, forkJoin, from, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators';
import { Error } from 'tslint/lib/error';

import { Action } from '../../actions';
import { changeLocation, showCommonDataFetchError } from '../../actions/common';
import { RequestGridRow } from '../../actions/grid-screen';
import {
    changeStatusPricePackage,
    PRICE_PACKAGE_SCREEN_GO_TO_PACKAGE_EDIT,
    PricePackageScreenGoToPackageEditAction,
    REQUSET_PRICE_PACKAGES_ROW,
    RESET_PRICE_PACKAGES_DATA,
    resetPricePackagesData,
    SAVE_STATUS_PRICE_PACKAGE,
    SaveStatusPricePackage,
    UPDATE_PRICE_PACKAGES_SOURCE_PARAMS,
    updatePricePackagesData,
    UpdatePricePackagesSourceParams
} from '../../actions/pricePackages';
import {
    closePricePackagesClientsModal,
    OPEN_PRICE_PACKAGES_CLIENTS_MODAL,
    OpenPricePackagesClientsModal,
    SAVE_PRICE_PACKAGES_CLIENTS_MODAL,
    setPricePackagesClientsModalDataAction,
    showPricePackagesClientsModalError
} from '../../actions/pricePackagesClientsModal';
import { LEAVE_PRICE_PACKAGES_EDIT, LeavePricePackagesEditAction } from '../../actions/pricePackagesEdit';
import { PricePackagesQueryResponse } from '../../api/pricePackages/pricePackagesDataProvider';
import { mapPricePackageDataToApi } from '../../mappers/pricePackages/pricePackages';
import { getPricePackagesClientsModalData } from '../../selectors/pricePackages/pricePackages';
import { ApiMethodExecutingError } from '../../service/Errors';
import { showNoticeMessage } from '../../service/GridHelpers/GridHelpers';
import {
    compactPricePackagesClientsError,
    mapPricePackagesValidationErrors,
    packagePriceErrorMapper
} from '../../service/PricePackagesHelpers/PricePackagesErrorMapper/PricePackagesErrorMapper';
import { RequestIndexResult } from '../../service/SegmentManager';
import { EpicDeps } from '../../service/StoreFactory';
import { mapTagValueToFirstItem } from '../../service/TagCloudTargetingHelper/TagCloudTargetingHelper';
import { UrlHelpers } from '../../service/UrlHelpers';
import { DnaEpic } from '../../typings/epic';
import { UpdatePricePackages } from '../../typings/graphql-grid';
import { NoticeType } from '../../typings/grid';
import { PricePackagesEditScreenUrlSearchParams } from '../../typings/pricePackages';
import { PricePackagesClientsModalState } from '../../typings/pricePackages';
import { State } from '../../typings/state';
import { TargetingTagType } from '../../typings/tagCloudTargeting';

import * as keyset from './pricePackages.i18n';

const i18n = i18nFactory(keyset);

const requestPricePackagesEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(REQUSET_PRICE_PACKAGES_ROW),
        map(({ index }: RequestGridRow) => {
            return deps.segmentManager.requestIndex(index);
        }),
        filter(res => res !== null),
        filter(({ isNeedFetch }: RequestIndexResult) => isNeedFetch),
        mergeMap(
            ({ offset, count }: RequestIndexResult): Observable<Action> => {
                const state = store$.value;
                const { sourceParams } = state.pricePackages;

                return from(
                    deps.pricePackagesDataProvider.fetchPricePackagesGridDataSegment(sourceParams, offset, count)
                ).pipe(
                    takeUntil(action$.ofType(RESET_PRICE_PACKAGES_DATA)),
                    mergeMap(
                        (response: PricePackagesQueryResponse): Observable<Action> => {
                            const storeData = store$.value;
                            const data = response.data.data.reduce((res, item, pos) => {
                                res[offset + pos] = item;

                                return res;
                            }, storeData.pricePackages.gridData.data.slice());

                            deps.segmentManager.setTotal(response.data.total);

                            return of(
                                updatePricePackagesData({
                                    data: data,
                                    total: response.data.total,
                                    allItemsIds: response.data.allItemsIds
                                })
                            );
                        }
                    )
                );
            }
        ),
        catchError(
            (err: ApiMethodExecutingError): Observable<Action> => {
                return of(showCommonDataFetchError(err));
            }
        )
    );
};

const saveStatusPricePackageEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_STATUS_PRICE_PACKAGE),
        mergeMap(
            (action: SaveStatusPricePackage): Observable<Action> => {
                const state = store$.value;
                const { login } = state.commonData;

                const pricePackagesData = state.pricePackages.gridData.data[action.index];

                return from(
                    deps.pricePackagesMutationExecutor.updatePricePackagesData(
                        mapPricePackageDataToApi(pricePackagesData, action.status),
                        login
                    )
                ).pipe(
                    mergeMap(
                        (response: FetchResult<UpdatePricePackages>): Observable<Action> => {
                            const updateErrors = get(response, 'data.updatePricePackages.validationResult.errors', []);

                            if (updateErrors.length) {
                                showNoticeMessage(
                                    NoticeType.error,
                                    i18n('change-status-error', { name: pricePackagesData.title })
                                );
                                return EMPTY;
                            }

                            showNoticeMessage(
                                NoticeType.success,
                                i18n('change-status-success', { name: pricePackagesData.title })
                            );

                            return of(changeStatusPricePackage(action.index, action.status));
                        }
                    )
                );
            }
        )
    );
};

const updatePricePackagesSourceParamsEpic: Epic<Action, Action, State, EpicDeps> = action$ => {
    return action$.pipe(
        ofType(UPDATE_PRICE_PACKAGES_SOURCE_PARAMS),
        map((action: UpdatePricePackagesSourceParams): Action => resetPricePackagesData())
    );
};

// todo DIRECT-78850
const resetPricePackagesDataEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    action$.ofType(RESET_PRICE_PACKAGES_DATA).subscribe(() => {
        deps.segmentManager.reset();
    });

    return EMPTY;
};

const gridScreenGoToPackageEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(PRICE_PACKAGE_SCREEN_GO_TO_PACKAGE_EDIT),
        switchMap(
            (action: PricePackageScreenGoToPackageEditAction): Observable<Action> => {
                const { login, id } = action;

                if (!login) {
                    throw new Error(`Не указан логин клиента`);
                }

                if (!id) {
                    return of(
                        changeLocation(
                            UrlHelpers.getPackageEditUrl({
                                [PricePackagesEditScreenUrlSearchParams.isNew]: '1',
                                [PricePackagesEditScreenUrlSearchParams.ulogin]: login
                            }),
                            undefined,
                            false
                        )
                    );
                }

                return of(
                    changeLocation(
                        UrlHelpers.getPackageEditUrl({
                            [PricePackagesEditScreenUrlSearchParams.pricePackagesIds]: id,
                            [PricePackagesEditScreenUrlSearchParams.ulogin]: login
                        }),
                        undefined,
                        false
                    )
                );
            }
        )
    );
};

const leavePricePackageEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(LEAVE_PRICE_PACKAGES_EDIT),
        switchMap(
            (action: LeavePricePackagesEditAction): Observable<Action> => {
                return of(changeLocation(UrlHelpers.getPricePackagesGridUrl()));
            }
        )
    );
};

const openPricePackagesClientsModalEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofType(OPEN_PRICE_PACKAGES_CLIENTS_MODAL),
        map(
            (action: OpenPricePackagesClientsModal): Action => {
                const state = store$.value;
                const modalData: PricePackagesClientsModalState = getPricePackagesClientsModalData(state, action.id);

                return setPricePackagesClientsModalDataAction(modalData);
            }
        )
    );
};

const savePricePackagesClientsModalEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_PRICE_PACKAGES_CLIENTS_MODAL),
        mergeMap(
            (): Observable<Action> => {
                const state = store$.value;
                const { login } = state.commonData;
                const {
                    pricePackagesClientsModal: { tags, id, lastUpdateTime, title }
                } = state;

                if (!id) {
                    return of(closePricePackagesClientsModal());
                }

                const getClientsIds = tags
                    .filter(tag => {
                        return tag.type === TargetingTagType.INCLUDED;
                    })
                    .map(tag => {
                        return mapTagValueToFirstItem(tag.value);
                    });

                const request$ = deps.pricePackagesMutationExecutor.updatePricePackagesData(
                    {
                        updateItems: [
                            {
                                id: id,
                                clientIds: getClientsIds,
                                lastSeenUpdateTime: lastUpdateTime
                            }
                        ]
                    },
                    login
                );

                return forkJoin(request$).pipe(
                    mergeMap(
                        (response): Observable<Action> => {
                            const updateErrors = get(
                                response[0],
                                'data.updatePricePackages.validationResult.errors',
                                []
                            );

                            if (updateErrors.length) {
                                const compactErrors = compactPricePackagesClientsError(updateErrors, [getClientsIds]);
                                const errors = mapPricePackagesValidationErrors(compactErrors, [id]);

                                return of(showPricePackagesClientsModalError(values(errors[id])));
                            }

                            showNoticeMessage(NoticeType.success, i18n('add-clients-success', { name: title }));

                            return of(resetPricePackagesData(), closePricePackagesClientsModal());
                        }
                    ),
                    catchError(
                        (err: ApiMethodExecutingError): Observable<Action> => {
                            return of(
                                showPricePackagesClientsModalError([
                                    {
                                        text: packagePriceErrorMapper({
                                            code: 'API_ERROR',
                                            params: '',
                                            path: 'commonPackagesErrors'
                                        })
                                    }
                                ])
                            );
                        }
                    )
                );
            }
        )
    );
};

export {
    resetPricePackagesDataEpic,
    requestPricePackagesEpic,
    updatePricePackagesSourceParamsEpic,
    saveStatusPricePackageEpic,
    gridScreenGoToPackageEditEpic,
    leavePricePackageEditEpic,
    openPricePackagesClientsModalEpic,
    savePricePackagesClientsModalEpic
};
